/**
 * The Firebase side of push: obtaining and revoking an FCM token.
 *
 * **Why the SDK is imported dynamically.** `firebase/app` plus `firebase/messaging` is a couple
 * of hundred kilobytes of JavaScript that the large majority of sessions never need — push is
 * opt-in, and somebody who never turns it on must not pay for it on first paint. Every import
 * here sits behind `await import(…)`, so Vite emits it as its own chunk that is fetched the first
 * time a token is actually wanted.
 *
 * **Why the config is committed.** Every value below ships inside the client bundle of any web
 * Firebase app and is readable by anyone who opens devtools; Firebase's own documentation says as
 * much. `apiKey` here is a project *identifier*, not a credential — what actually authorises
 * sending is the service account on the backend, which is not in this repository. Keeping these
 * six lines in the source rather than in six CI variables removes a class of broken build where
 * one variable is missing and push silently stops working in production only.
 */

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyBsImbZ6TUVstpZHOxvw2oNCf9MNETjdG8',
  authDomain: 'twinturbo-a92d0.firebaseapp.com',
  projectId: 'twinturbo-a92d0',
  storageBucket: 'twinturbo-a92d0.firebasestorage.app',
  messagingSenderId: '446603720483',
  appId: '1:446603720483:web:0f111e50f43748e1a63e96',
} as const;

/** Raised when the SDK itself declares the browser incapable, after our own checks passed. */
export class PushUnsupportedError extends Error {
  constructor() {
    super('Браузер не поддерживает push-уведомления');
    this.name = 'PushUnsupportedError';
  }
}

async function messaging() {
  const [{ initializeApp, getApps }, msg] = await Promise.all([
    import('firebase/app'),
    import('firebase/messaging'),
  ]);

  // `isSupported()` covers what feature detection cannot: private windows in some browsers, and
  // builds where the push service is reachable but the messaging transport is not.
  if (!(await msg.isSupported())) throw new PushUnsupportedError();

  // Re-initialising the same app throws. Two callers can race here (a sync on start and a tap on
  // the banner), so reuse whatever is already up.
  const app = getApps()[0] ?? initializeApp(FIREBASE_CONFIG);
  return { msg, instance: msg.getMessaging(app) };
}

/**
 * The FCM registration token for this browser.
 *
 * Takes our own service-worker registration rather than letting the SDK go looking. Left alone it
 * fetches `/firebase-messaging-sw.js`, registers it as a *second* worker on the origin, and the
 * app ends up with two workers fighting over the same scope — one of them precaching the shell
 * and one of them handling push, each unaware of the other. Passing the registration keeps
 * everything in the single worker built from `src/sw.ts`.
 */
export async function obtainFcmToken(registration: ServiceWorkerRegistration): Promise<string> {
  const { msg, instance } = await messaging();
  const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

  return msg.getToken(instance, {
    serviceWorkerRegistration: registration,
    ...(vapidKey ? { vapidKey } : {}),
  });
}

/**
 * Drops the token so this browser stops receiving pushes for the account that just left.
 *
 * There is no server-side unregister endpoint (`/v1/consumer/device-tokens` is POST-only), and it
 * turns out none is needed: once the token is deleted here, FCM answers the backend's next send
 * with `UNREGISTERED`, and the consumer push worker prunes the row itself. The cleanup is
 * self-healing rather than something a failed logout request can leave behind.
 */
export async function revokeFcmToken(): Promise<void> {
  const { msg, instance } = await messaging();
  await msg.deleteToken(instance);
}
